import { useState } from 'react';

import { BpkDropdownChip } from '@skyscanner-internal/backpack-web/bpk-component-chip';
import BpkPopover from '@skyscanner-internal/backpack-web/bpk-component-popover';
import { cssModules } from '@skyscanner-internal/backpack-web/bpk-react-utils';
import { useTranslations } from '@web-platform/shared-travel-tools-utils-frontend';
import {
  useUrlCountryName,
  useOriginCityEntityId,
  useOriginContext,
} from '@web-platform/travel-tools-flight-price-map-data-access-frontend';
import {
  type CityItem,
  getOriginName,
} from '@web-platform/travel-tools-flight-price-map-utils-frontend';

import { OriginAutosuggest } from '../OriginAutosuggest/OriginAutosuggest';
import { OriginAutosuggestModal } from '../OriginAutosuggestModal/OriginAutosuggestModal';

import STYLES from './OriginSelector.module.scss';

const cls = cssModules(STYLES);

export type OriginSelectorProps = {
  cities: CityItem[];
  isMobile: boolean;
};

export const OriginSelector = ({ cities, isMobile }: OriginSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { translate } = useTranslations();
  const originContext = useOriginContext();
  const cityEntityId = useOriginCityEntityId();
  const originName = getOriginName(originContext, cityEntityId ?? undefined);
  const countrySlug = useUrlCountryName();
  const fromFilterLabel = translate('travel_confidence_map_filter_from_label', {
    destination: originName,
  });

  const handleClose = () => setIsOpen(false);
  const handleToggle = () => setIsOpen((prev) => !prev);
  const handleSelect = () => {
    setIsOpen(false);
  };

  const chip = (
    <BpkDropdownChip
      accessibilityLabel={fromFilterLabel}
      aria-expanded={isOpen}
      aria-haspopup="dialog"
      onClick={handleToggle}
      role="button"
      selected
      type={cityEntityId ? 'default' : 'on-dark'}
    >
      {fromFilterLabel}
    </BpkDropdownChip>
  );

  return (
    <div className={cls('container')}>
      {isMobile ? (
        <>
          <div className={cls('chipWrapper')}>{chip}</div>
          <OriginAutosuggestModal
            cities={cities}
            countryName={originContext?.countryName}
            countrySlug={countrySlug}
            inputPlaceholder={originName || undefined}
            isOpen={isOpen}
            onClose={handleClose}
            onSelect={handleSelect}
          />
        </>
      ) : (
        <BpkPopover
          className={STYLES.popover}
          closeButtonIcon={false}
          id="travel-confidence-map-origin-selector"
          isOpen={isOpen}
          label={translate('travel_confidence_map_where_from')}
          labelAsTitle
          onClose={handleClose}
          padded={false}
          placement="bottom-start"
          showArrow={false}
          target={<div className={cls('chipWrapper')}>{chip}</div>}
        >
          <OriginAutosuggest
            cities={cities}
            countryName={originContext?.countryName}
            countrySlug={countrySlug}
            onClose={handleClose}
            onSelect={handleSelect}
          />
        </BpkPopover>
      )}
    </div>
  );
};
